"use client"

import type React from "react"

import * as X from "@/ui"
import { links } from "@/config";
import { menu, useRoute, LanguageSelectorMobile } from "./common"

import ZapptaxLogo from "@/ui/shared/assets/zapptax-inverted.svg";
import GetAppStore from "@/ui/shared/assets/get-app-store.svg";
import GetPlayStore from "@/ui/shared/assets/get-play-store.svg";

import SocialInstagram from "./assets/social-instagram.svg"
import SocialFacebook from "./assets/social-facebook.svg"
import SocialLinkedIn from "./assets/social-linkedin.svg"
import SocialRedNote from "./assets/social-rednote.svg"
import SocialWeChat from "./assets/social-wechat.svg"

import FlagFr from "./assets/flag-fr.svg"
import FlagBe from "./assets/flag-be.svg"
import FlagEs from "./assets/flag-es.svg"
import FlagUk from "./assets/flag-uk.svg"

import CompSpfFinances from "./assets/compliancy-spf-finances.svg"
import CompAgenciaTributaria from "./assets/compliancy-agencia-tributaria.svg"

import SuccessIcon from "@/ui/shared/assets/newsletter-success.svg";

import FakeTrustpilot from "./assets/fake-trustpilot-widget.svg";


export function FooterMobile(props: X.PageProps<{
  className?: string;
  altHrefs?: { [k in X.Locale]: X.PageHref };
}>) {
  const [section] = useRoute();
  const isRetail = section === "retail" || section === "shipping";

  return (
    <footer className={X.clsx("xl:hidden bg-black text-white", props.className)}>
      <div className="px-mobile-margin py-mobile-margin">
        <div className="flex flex-row justify-between items-center">
          <LanguageSelectorMobile
            locale={props.locale}
            altHrefs={props.altHrefs}
            backgroundColor="black"
          />

          <X.Link href="/" className="flex justify-center my-6">
            <X.Image src={ZapptaxLogo} alt="Zapptax" height={24} className="w-auto" />
          </X.Link>
        </div>

        <Sitemap locale={props.locale} />
        <TrustpilotAndSocial />
        <Contact isRetail={isRetail} />
        <GetTheApp locale={props.locale} />
        {isRetail ? <BecomePartner /> : <Newsletter />}
        <Compliance />
        <Legal />
      </div>
    </footer>
  );
}


function Sitemap(props: X.PageProps) {
  const tr = X.useTranslator(props.locale);
  const [openSection, setOpenSection] = X.useState<string | null>(null);

  const toggleSection = (name: string) => {
    if (openSection === name) {
      setOpenSection(null)
    } else {
      setOpenSection(name)
    }
  }

  return (
    <div className="border-b border-zinc-800 divide-y divide-zinc-800">
      {Object.entries(menu)
        .filter(([_, s]) => s.showFooter)
        .map(([name, section]) => (
          <div key={name} className="py-4">
            <button className="flex justify-between items-center w-full" onClick={() => toggleSection(name)}>
              <span className="text-base font-medium">{tr(section.label)}</span>
              <X.Icon.ChevronDown
                size={20}
                className={X.clsx("transition-transform", openSection === name ? "rotate-180" : "")}
              />
            </button>

            {openSection === name && (
              <div className="mt-3 space-y-3 py-2">
                {Object.entries(section.sub)
                  .filter(([_, sub]) => sub.type !== "button")
                  .map(([key, sub]) => (
                    <X.Link key={key} href={sub.href} className="block text-sm text-zinc-300">
                      {tr(sub.label)}
                    </X.Link>
                  ))}
              </div>
            )}
          </div>
        ))}
    </div>
  );
}


function TrustpilotAndSocial() {
  return (
    <div className="py-6 flex flex-row justify-between items-center">
      <X.ExternalLink href="https://www.trustpilot.com/review/www.zapptax.com">
        <X.Image
          src={FakeTrustpilot}
          alt="Trustpilot"
          height={70}
          className="translate-y-1"
        />
      </X.ExternalLink>

      <div className="flex justify-center space-x-4 mt-4">
        {[
          { image: SocialInstagram, link: links.social.instagram, name: "Instagram" },
          { image: SocialFacebook, link: links.social.facebook, name: "Facebook" },
          { image: SocialLinkedIn, link: links.social.linkedIn, name: "LinkedIn" },
          { image: SocialWeChat, link: links.social.weChat, name: "WeChat" },
          { image: SocialRedNote, link: links.social.redNote, name: "RedNote" },
        ].map((social, i) => (
          social.link.startsWith("http") ? (
            <X.ExternalLink key={i} href={social.link}>
              <X.Image src={social.image} alt={social.name} height={19} className="w-auto" />
            </X.ExternalLink>
          ) : (
            <a key={i} href={social.link}>
              <X.Image src={social.image} alt={social.name} height={19} className="w-auto" />
            </a>
          )
        ))}
      </div>
    </div>
  );
}


function Contact(props: {
  isRetail: boolean;
}) {
  const tr = X.useTranslator();

  const phones = (
    props.isRetail
      ? [
        [
          { phone: links.phone.retail.france, flag: FlagFr, name: tr({ en: "France", fr: "France" }) },
          { phone: links.phone.retail.belgium, flag: FlagBe, name: tr({ en: "Belgium", fr: "Belgique" }) },
        ],
        [
          { phone: links.phone.retail.spain, flag: FlagEs, name: tr({ en: "Spain", fr: "Espagne" }) },
        ],
      ]
      : [
        [
          { phone: links.phone.retail.france, flag: FlagFr, name: tr({ en: "France", fr: "France" }) },
          { phone: links.phone.traveler.uk, flag: FlagUk, name: tr({ en: "UK", fr: "Royaume-Uni" }) },
        ],
        [
          { phone: links.phone.retail.spain, flag: FlagEs, name: tr({ en: "Spain", fr: "Espagne" }) },
          { phone: links.phone.retail.belgium, flag: FlagBe, name: tr({ en: "Belgium", fr: "Belgique" }) },
        ],
      ]
  );

  return (
    <div className="py-6 flex flex-col gap-4">
      <h3 className="text-[16px] font-medium mb-2">
        {tr({
          en: "Contact Us",
          fr: "Contactez-nous"
        })}
      </h3>

      <div className="flex flex-col gap-4 text-[14px] font-semibold">
        <X.ExternalLink href={`mailto:${links.email.support}`} className="block">
          Email
        </X.ExternalLink>
        <div className="X-Open-Intercom block">
          Live chat
        </div>
        <a href={links.social.weChat} className="block">
          WeChat
        </a>
      </div>

      <div className="space-y-4">
        <div className="text-[14px] font-semibold">
          {tr({
            en: "Phone",
            fr: "Téléphone",
          })}
        </div>

        <div className="flex flex-col pt-4 gap-4">

          {phones.map((group, i) => (
            <div key={i} className="flex flex-row gap-4 text-[12px] font-light">
              {group.map((country, i) => (
                <a key={i} className="flex flex-col flex-1" href={`tel:${country.phone.replace(/\s+/g, "")}`}>
                  <div className="flex items-center gap-2">
                    <X.Image src={country.flag} height={10} alt="France" />
                    <span>{country.name}</span>
                  </div>
                  <div>{country.phone}</div>
                </a>
              ))}
            </div>
          ))}

        </div>
      </div>
    </div>
  );
}


function GetTheApp(props: X.PageProps) {
  const downloadUrl = X.useDownloadUrl(props.locale);

  return (
    <div className="py-6 flex flex-col items-center">
      <div className="flex gap-4">
        <X.ExternalLink href={downloadUrl}>
          <X.Image src={GetAppStore} alt="Download on App Store" height={45} />
        </X.ExternalLink>
        <X.ExternalLink href={downloadUrl}>
          <X.Image src={GetPlayStore} alt="Get it on Google Play" height={45} />
        </X.ExternalLink>
      </div>
    </div>
  );
}


function Newsletter() {
  const tr = X.useTranslator();
  const form = X.useNewsletterForm();

  return (
    <div className="mt-4 max-w-[520px] gap-2 flex flex-col">
      <h3 className="text-[16px] mb-2">
        {tr({
          en: "Stay in the loop. Subscribe to our newsletter",
          fr: "Suivez nos dernières actualités",
        })}
      </h3>
      {form.isSent() ? (
        <div className={X.clsx(
          "flex flex-row items-center justify-center bg-[#404040] text-white p-2 rounded-[8px] w-full text-[12px] h-[52px]"
        )}>
          <X.Image src={SuccessIcon} alt={tr({ en: "Success", fr: "Succès" })} className="w-[16px] h-auto mr-3" />
          <div className="text-[14px] font-bold text-center mr-3">{tr({ en: "Thank you!", fr: `Merci${X.Nbsp}!` })}</div>
          <div className="text-[12px]">{tr({
            en: "You are successfully subscribed to our newsletter",
            fr: "Nous avons bien enregistré votre inscription à notre newsletter",
          })}</div>
        </div>

      ) : (
        <form className="flex" onSubmit={form.handleSubmit}>
          <div className={X.clsx(
            "flex flex-row bg-[#404040] text-white rounded-[8px] w-full text-[12px] relative items-center border",
            form.hasTouchedError() ? "border-danger" : "border-transparent",
          )}>
            <input
              type="email"
              placeholder={tr({ en: "Your email address", fr: "Votre e-mail" })}
              className="text-white w-full text-[14px] px-4 h-[52px] outline-none"
              onChange={form.handleChangeEmail}
              onBlur={form.handleBlur}
              value={form.getEmail()}
              required
            />
            <X.Button
              secondary
              label={tr({ en: "Sign Up", fr: "Je m'inscris" })}
              className="px-4 py-2 mr-2 whitespace-nowrap font-medium text-[14px]"
              processing={form.isSubmitting()}
              disabled={!form.canSubmit()}
              submit
            />
          </div>
        </form>
      )}
      <p className="text-[10px] mt-2 text-zinc-400">
        {tr({
          en: "By entering my email address, I agree to receive information from Zapptax and confirm I have read the privacy policy.",
          fr: "En communiquant mon adresse e-mail, j'accepte de recevoir des informations de la part de Zapptax et je reconnais avoir pris connaissance de la politique de confidentialité.",
        })}
      </p>
    </div>
  );
}


function BecomePartner() {
  const tr = X.useTranslator();

  return (
    <div className="py-6">
      <p className="mb-4">
        {tr({
          en: "Are you a retailer interested in Zapptax?",
          fr: "Vous êtes un commerçant et Zapptax vous intéresse ?",
        })}
      </p>
      <X.Button
        label={tr({ en: "Become a partner", fr: "Devenir partenaire" })}
        className="px-6 py-2 font-medium text-sm bg-white text-black rounded-md hover:bg-zinc-200"
        href="/retail/become-partner"
      />
    </div>
  );
}


function Compliance() {
  const tr = X.useTranslator();

  return (
    <div className="py-6">
      <h3 className="text-sm font-medium mb-4">
        {tr({
          en: "In compliance with regulations by",
          fr: "En conformité avec les réglementations établies par",
        })}
      </h3>
      <div className="flex justify-between space-x-4 mb-6">
        <X.ExternalLink href="http://www.agenciatributaria.es/">
          <X.Image src={CompAgenciaTributaria} alt="Agencia Tributaria" height={50} />
        </X.ExternalLink>

        <X.ExternalLink href="https://finances.belgium.be/">
          <X.Image src={CompSpfFinances} alt="SPF Finances" height={50} />
        </X.ExternalLink>
      </div>

      <div className="text-xs text-zinc-400 flex flex-col gap-1">
        <p>
          {tr({
            en: "Zapptax is a registered trademark of ZAPPTAX SA Company ID: BE 0670 776 774",
            fr: "Zapptax est une marque déposée de ZAPPTAX SA enregistrée sous le numéro ID BE 0670 776 774",
          })}
        </p>
        <p>
          {tr({
            en: "Registered office: Rue du Boulet, 42 1000 Brussels, BELGIUM",
            fr: "Siège social: Rue du Boulet, 42 1000 BRUXELLES BELGIQUE",
          })}
        </p>
      </div>
    </div>
  );
}


function Legal() {
  const tr = X.useTranslator();
  const year = new Date().getFullYear();

  return (
    <div className="pt-6 text-[10px]">
      <div className="flex justify-between mb-4">
        <X.Link href="/terms-and-conditions">
          {tr({
            en: "Terms of Use",
            fr: "Conditions Générales d'utilisation",
          })}
        </X.Link>
        <X.Link href="/privacy-policy">
          {tr({
            en: "Privacy Policy",
            fr: "Politique de confidentialité",
          })}
        </X.Link>
      </div>
      <div className="text-zinc-400 font-medium">
        {tr({
          en: `© Copyright 2017-${year}, Zapptax S.A. All rights reserved.`,
          fr: `© Copyright 2017-${year}, Zapptax S.A. Tous droits réservés.`,
        })}
      </div>
    </div>
  );
}
